import { BarChart, PivotTableChart } from "@mui/icons-material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import ArticleIcon from "@mui/icons-material/Article";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CommentIcon from "@mui/icons-material/Comment";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EditNotificationsIcon from "@mui/icons-material/EditNotifications";
import EmailIcon from "@mui/icons-material/Email";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import Face3Icon from "@mui/icons-material/Face3";
import FlagIcon from "@mui/icons-material/Flag";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HistoryIcon from "@mui/icons-material/History";
import HomeIcon from "@mui/icons-material/Home";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import KeyIcon from "@mui/icons-material/Key";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import SendIcon from "@mui/icons-material/Send";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import { Badge } from "@mui/material";
import { Permissions, ROLES } from "app/constants";
import { useAppSelector } from "app/hooks";
import NestedSidebarNavigation, {
  NestedSidebarItem,
} from "common/components/Navigation/NestedSidebarNavigation";
import useNotifyCount from "features/bookings/useNotifyCount";
import useRefundEligibleCount from "features/bookings/useRefundEligibleCount";
import useWebCommentCount from "features/bookings/useWebCommentCount";
import useInvoiceNeededCount from "features/payment-transactions/useInvoiceNeededCount";
import { selectProfile } from "features/profile/profileSlice";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import SidebarShellV2 from "layouts/SidebarV2/SidebarShellV2";
import AdminUserMenu from "./AdminUserMenu";

export interface AdminSidebarMenuV2Props {
  open?: boolean;
  temporary?: boolean;
  setOpen?: (open?: boolean) => void;
}

export default function AdminSidebarMenuV2({
  open = false,
  temporary = false,
  setOpen,
}: AdminSidebarMenuV2Props) {
  const profile = useAppSelector(selectProfile);
  const { hasPermissions, hasRoles } = useAccess();
  const invoiceNeededCount = useInvoiceNeededCount();
  const notifyCount = useNotifyCount();
  const webCommentCount = useWebCommentCount();
  const refundEligibleCount = useRefundEligibleCount();

  const items: NestedSidebarItem[] = [
    {
      id: "home",
      label: I18n.t("Home.title"),
      path: "/",
      exact: true,
      icon: <HomeIcon />,
      visible: hasPermissions(Permissions.CalendarMenu),
    },
    {
      id: "booking-list",
      label: I18n.t("Booking.title"),
      path: "/bookings",
      icon: <KeyIcon />,
      visible: hasPermissions("booking.menu"),
    },
    {
      id: "events",
      label: I18n.t("Event.title"),
      path: "/events",
      icon: <CalendarMonthIcon />,
      visible: hasPermissions("event.menu"),
    },
    {
      id: "customer-care",
      label: I18n.t("Sidebar.customerCare"),
      icon: <SupportAgentIcon />,
      children: [
        {
          id: "notify-bookings",
          label: I18n.t("Booking.shouldNotifyTitle"),
          path: "/notify-bookings",
          icon: (
            <Badge
              badgeContent={notifyCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  right: -14,
                  top: 1,
                  minWidth: 16,
                  height: 16,
                  px: "4px",
                  fontSize: 10,
                },
              }}
            >
              <PublishedWithChangesIcon />
            </Badge>
          ),
          visible: hasPermissions(Permissions.BookingNotify),
        },
        {
          id: "web-comment-bookings",
          label: I18n.t("Booking.webCommentTitle"),
          path: "/web-comment-bookings",
          icon: (
            <Badge badgeContent={webCommentCount} color="error">
              <CommentIcon />
            </Badge>
          ),
          visible: hasPermissions("booking.menu"),
        },
        {
          id: "refund-eligible-bookings",
          label: I18n.t("Booking.refundEligibleTitle"),
          path: "/refund-eligible-bookings",
          icon: (
            <Badge badgeContent={refundEligibleCount} color="error">
              <MoneyOffIcon />
            </Badge>
          ),
          visible: hasPermissions("booking.menu"),
        },
        {
          id: "review-email-sents",
          label: I18n.t("ReviewEmailSent.title"),
          path: "/review-email-sents",
          icon: <EmailIcon />,
          visible: hasPermissions("review_partner.menu"),
        },
        {
          id: "email-management",
          label: I18n.t("Sidebar.emailManagement"),
          icon: <EmailIcon />,
          children: [
            {
              id: "email-templates",
              label: I18n.t("EmailTemplate.title"),
              path: "/email-templates",
              icon: <EmailIcon />,
              visible: hasPermissions("email_template.menu"),
            },
            {
              id: "bulk-email-sents",
              label: I18n.t("BulkEmailSent.title"),
              path: "/bulk-emails-sent",
              icon: <SendIcon />,
              visible: hasPermissions("bulk_email_sent.menu"),
            },
          ],
        },
      ],
    },
    {
      id: "catering-group",
      label: I18n.t("Catering.title"),
      icon: <RestaurantMenuIcon />,
      children: [
        {
          id: "catering",
          label: I18n.t("Catering.title"),
          path: "/catering",
          icon: <EventSeatIcon />,
          visible: hasPermissions(Permissions.CateringMenu),
        },
        {
          id: "kitchen",
          label: I18n.t("Kitchen.title"),
          path: "/kitchen",
          icon: <RestaurantMenuIcon />,
          visible: hasPermissions(Permissions.KitchenMenu),
        },
        {
          id: "seating-layouts",
          label: I18n.t("SeatingLayout.title"),
          path: "/seating-layouts",
          icon: <EventSeatIcon />,
          visible: hasPermissions("seating_layout.menu"),
        },
      ],
    },
    {
      id: "coordinator",
      label: I18n.t("Sidebar.coordinator"),
      icon: <Diversity3Icon />, //CSERE
      children: [
        {
          id: "host",
          label: I18n.t("Host.title"),
          path: "/host",
          icon: <Face3Icon />,
          visible: hasPermissions(Permissions.HostMenu),
        },
        {
          id: "street-sales",
          label: I18n.t("StreetSales.title"),
          path: "/street-sales",
          icon: <StorefrontIcon />,
          visible: hasPermissions(Permissions.StreetMenu),
        },
        {
          id: "sales-persons",
          label: I18n.t("SalesPerson.title"),
          path: "/sales-persons",
          icon: <LoyaltyIcon />,
          visible: hasPermissions("sales_person.menu"),
        },
      ],
    },
    {
      id: "sales",
      label: I18n.t("Sidebar.sales"),
      icon: <HandshakeIcon />,
      children: [
        {
          id: "booking-documents",
          label: I18n.t("BookingDocument.title"),
          path: "/booking-documents",
          icon: <ArticleIcon />,
          visible: hasPermissions("booking_document.menu"),
        },
        {
          id: "partner-feeds",
          label: I18n.t("PartnerFeed.title"),
          path: "/partner-feeds",
          icon: <RssFeedIcon />,
          visible: hasPermissions("partner_feed.menu"),
        },
        {
          id: "partners-group",
          label: I18n.t("Partner.title"),
          icon: <HandshakeIcon />,
          children: [
            {
              id: "partners",
              label: I18n.t("Partner.title"),
              path: "/partners",
              icon: <HandshakeIcon />,
              visible: hasPermissions("partner.menu"),
            },
            {
              id: "redflag-partners",
              label: I18n.t("RedflagPartner.title"),
              path: "/redflag-partners",
              icon: <FlagIcon color="error" />,
              visible: hasPermissions("redflag_partner.menu"),
            },
          ],
        },
      ],
    },
    {
      id: "reports",
      label: I18n.t("Sidebar.reports"),
      icon: <InsightsRoundedIcon />,
      children: [
        {
          id: "coordinator-reports",
          label: I18n.t("Sidebar.coordinatorReports"),
          icon: <FolderOpenRoundedIcon />,
          children: [
            {
              id: "coordinator-report",
              label: I18n.t("CoordinatorReport.title"),
              path: "/reports/coordinator",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_coordinator"),
            },
            {
              id: "coordinator-street-report",
              label: I18n.t("CoordinatorReport2.title"),
              path: "/reports/coordinator-2",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_coordinator_street"),
            },
            {
              id: "coordinator-web-report",
              label: I18n.t("CoordinatorReport3.title"),
              path: "/reports/coordinator-3",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_coordinator_web"),
            },
            {
              id: "daily-trips-report",
              label: I18n.t("DailyTripsReport.title"),
              path: "/reports/daily-trips",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_daily_trips"),
            },
          ],
        },
        {
          id: "sales-reports",
          label: I18n.t("Sidebar.salesReports"),
          icon: <FolderOpenRoundedIcon />,
          children: [
            {
              id: "sales-report",
              label: I18n.t("SalesReport.title"),
              path: "/reports/sales",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_sales"),
            },
            {
              id: "commission-report",
              label: I18n.t("CommissionReport.title"),
              path: "/reports/commission",
              icon: <AssessmentIcon />,
              visible: hasPermissions("report.menu_commission"),
            },
          ],
        },
        {
          id: "business-reports",
          label: I18n.t("Sidebar.businessReports"),
          icon: <FolderOpenRoundedIcon />,
          children: [
            {
              id: "simple-report",
              label: I18n.t("SimpleReport.title"),
              path: "/reports/simple",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_simple"),
            },
            {
              id: "advanced-report",
              label: I18n.t("AdvancedReport.title"),
              path: "/reports/advanced",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_advanced"),
            },
            {
              id: "dynamic-pricing-report",
              label: I18n.t("DynamicPricingReport.title"),
              path: "/reports/dynamic-pricing",
              icon: <BarChart />,
              visible: hasPermissions("report.menu_advanced"),
            },
            {
              id: "controlling-report",
              label: I18n.t("PivotReport.title"),
              path: "/reports/controlling",
              icon: <PivotTableChart />,
              visible: hasPermissions("report.menu_controlling"),
            },
            {
              id: "rebook-fee-report",
              label: I18n.t("RebookFeeReport.title"),
              path: "/reports/rebook-fees",
              icon: <EventRepeatIcon />,
              visible: hasPermissions("report.menu_rebook_fees"),
            },
          ],
        },
      ],
    },
    {
      id: "finance",
      label: I18n.t("Sidebar.finance"),
      icon: <PaymentIcon />,
      children: [
        {
          id: "exchange-rates",
          label: I18n.t("ExchangeRate.title"),
          path: "/exchange-rates",
          icon: <CurrencyExchangeIcon />,
          visible: hasPermissions("exchange_rate.menu"),
        },
        {
          id: "payment-transactions",
          label: I18n.t("PaymentTransaction.title"),
          path: "/payment-transactions",
          icon: (
            <Badge badgeContent={invoiceNeededCount} color="error">
              <PaymentIcon />
            </Badge>
          ),
          visible: hasPermissions("payment_transaction.menu"),
        },
      ],
    },
    {
      id: "administration",
      label: I18n.t("Sidebar.administration"),
      icon: <AppRegistrationIcon />, //csere
      children: [
        {
          id: "access-control",
          label: I18n.t("AccessControl.title"),
          path: "/access-control",
          icon: <AdminPanelSettingsIcon />,
          visible: hasPermissions(["access_control.manage", "access_control.manage_countries"]) || hasRoles(ROLES.SUPERADMIN),
        },
        {
          id: "partner-groups",
          label: I18n.t("PartnerGroup.title"),
          path: "/partner-groups",
          icon: <WorkspacesIcon />,
          visible: hasPermissions("partner_group.menu"),
        },
        {
          id: "programs",
          label: I18n.t("Program.title"),
          path: "/programs",
          icon: <TheaterComedyIcon />,
          visible: hasPermissions("program.menu"),
        },
        {
          id: "option-groups",
          label: I18n.t("OptionGroup.title"),
          path: "/option-groups",
          icon: <WorkspacesIcon />,
          visible: hasPermissions("option_group.menu"),
        },
        {
          id: "boats",
          label: I18n.t("Boat.title"),
          path: "/boats",
          icon: <DirectionsBoatIcon />,
          visible: hasPermissions("boat.menu"),
        },
        {
          id: "schedules",
          label: I18n.t("Schedule.title"),
          path: "/schedules",
          icon: <EditCalendarIcon />,
          visible: hasPermissions("schedule.menu"),
        },
      ],
    },
    {
      id: "super-admin",
      label: I18n.t("Sidebar.superAdmin"),
      icon: <AdminPanelSettingsIcon />,
      visible: hasRoles([ROLES.SUPERADMIN]),
      children: [
        {
          id: "users",
          label: I18n.t("User.title"),
          path: "/users",
          icon: <PersonIcon />,
          visible: hasPermissions(["user.viewown", "user.viewany"]),
        },
        {
          id: "roles",
          label: I18n.t("Role.title"),
          path: "/roles",
          icon: <AdminPanelSettingsIcon />,
          visible: hasPermissions("role.view"),
        },
        {
          id: "history",
          label: I18n.t("History.title"),
          path: "/history",
          icon: <HistoryIcon />,
          visible: hasPermissions(["history.viewany", "history.viewown"]),
        },
        {
          id: "files",
          label: I18n.t("File.title"),
          path: "/files",
          icon: <AttachFileIcon />,
          visible: hasPermissions("file.view"),
        },
        {
          id: "email-preview",
          label: I18n.t("EmailPreview.title"),
          path: "/email-preview",
          icon: <MarkEmailReadIcon />,
          visible: hasPermissions("email_template.viewany"),
        },
        {
          id: "notification-addresses",
          label: I18n.t("NotificationAddress.title"),
          path: "/notification-addresses",
          icon: <EditNotificationsIcon />,
          visible: hasPermissions([
            "notification_address.viewown",
            "notification_address.viewany",
          ]),
        },
      ],
    },
  ];

  return (
    <SidebarShellV2
      open={open}
      temporary={temporary}
      setOpen={setOpen}
      profileSubtitle={profile.roles?.join(", ")}
      userMenu={<AdminUserMenu />}
      navigation={
        <NestedSidebarNavigation
          items={items}
          expanded={open}
          onRequestExpand={() => setOpen?.(true)}
          onNavigate={temporary ? () => setOpen?.(false) : undefined}
        />
      }
    />
  );
}
